<template>
  <header :class="{ header: true, navScroll }">
    <div class="header-top">
      <div class="header-inner">
        <router-link to="/" class="logo-link">
          <img :src="logo" alt="logo" class="logo-img" />
        </router-link>
        <div class="header-contact">
          <a :href="'tel:' + phoneTel" class="contact-item">
            <i class="el-icon-phone contact-icon"></i>
            <span>{{ $t("header.phone") }}</span>
          </a>
          <a :href="'mailto:' + $t('header.email')" class="contact-item">
            <i class="el-icon-message contact-icon"></i>
            <span>{{ $t("header.email") }}</span>
          </a>
        </div>
        <form class="header-search" @submit.prevent="onSearch">
          <input
            v-model="searchKeyword"
            type="search"
            class="search-input"
            :placeholder="$t('search.placeholder')"
          />
          <button
            type="submit"
            class="search-btn"
            :aria-label="$t('search.submit')"
          >
            <i class="el-icon-search"></i>
          </button>
        </form>
        <div class="lang-switch" @click="switchLang" :title="$t('lang.switch')">
          {{ $t("lang.switch") }}
        </div>
      </div>
    </div>
    <nav class="header-nav">
      <div class="header-inner nav-inner">
        <div
          v-for="item in navList"
          :key="item.key"
          :class="{
            'nav-item': true,
            'nav-item--has-child': item.subs,
            'nav-item--active': isNavActive(item),
          }"
        >
          <div class="nav-title" @click="navTo(item)">
            <span>{{ item.title }}</span>
            <i class="el-icon-arrow-down" v-if="item.subs"></i>
          </div>
          <div class="nav-subs" v-if="item.subs">
            <div
              class="nav-sub"
              v-for="child in item.subs"
              :key="child.path"
              @click="navTo(child)"
            >
              {{ child.title }}
            </div>
          </div>
        </div>
      </div>
    </nav>
  </header>
</template>

<script>
import { setPageLocale } from "@/i18n";
import logoEn from "@/assets/logo/logoEn.png";
import logoZh from "@/assets/logo/logoZh.png";

export default {
  data() {
    return {
      navScroll: false,
      searchKeyword: "",
    };
  },
  computed: {
    logo() {
      return this.$i18n.locale === "zh-CN" ? logoZh : logoEn;
    },
    phoneTel() {
      return this.$t("header.phone").replace(/\s/g, "");
    },
    navList() {
      return [
        { key: "home", title: this.$t("nav.home"), path: "/" },
        {
          key: "plan-trip",
          title: this.$t("nav.planTrip"),
          path: "/plan-trip",
        },
        {
          key: "destinations",
          title: this.$t("nav.destinations"),
          path: "/destination",
          subs: [
            {
              title: this.$t("nav.destSichuan"),
              path: "/destination/region/sichuan",
            },
            {
              title: this.$t("nav.destChongqing"),
              path: "/destination/region/chongqing",
            },
            {
              title: this.$t("nav.destBeijing"),
              path: "/destination/region/beijing",
            },
            {
              title: this.$t("nav.destXian"),
              path: "/destination/region/xian",
            },
          ],
        },
        {
          key: "travel-themes",
          title: this.$t("nav.travelThemes"),
          path: "/guide/srdz",
        },
        {
          key: "testimonials",
          title: this.$t("nav.testimonials"),
          path: "/",
          scrollTo: "testimonials",
        },
        { key: "about", title: this.$t("nav.about"), path: "/about" },
        { key: "contact", title: this.$t("nav.contact"), path: "/contact" },
      ];
    },
  },
  watch: {
    "$route.query.q"(val) {
      if (this.$route.path === "/search") {
        this.searchKeyword = val || "";
      }
    },
  },
  mounted() {
    if (this.$route.path === "/search") {
      this.searchKeyword = this.$route.query.q || "";
    }
    window.addEventListener("scroll", this.onScroll);
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.onScroll);
  },
  methods: {
    onScroll() {
      this.navScroll = window.scrollY >= 200;
    },
    switchLang() {
      const next = this.$i18n.locale === "zh-CN" ? "en-US" : "zh-CN";
      setPageLocale(next);
    },
    isNavActive(item) {
      if (item.scrollTo) {
        return this.$route.path === "/" && this.$route.hash === "#testimonials";
      }
      if (item.subs) {
        return this.$route.path.startsWith("/destination");
      }
      if (item.path === "/guide/srdz") {
        return this.$route.path === "/guide/srdz";
      }
      return item.path === this.$route.path;
    },
    navTo(item) {
      if (item.subs) return;
      if (item.scrollTo) {
        this.goToTestimonials();
        return;
      }
      if (this.$route.path !== item.path) {
        this.$router.push(item.path);
        window.scrollTo({ top: 0 });
      }
    },
    goToTestimonials() {
      const scroll = () => {
        const el = document.getElementById("testimonials");
        if (!el) return;
        const top = el.getBoundingClientRect().top + window.pageYOffset - 140;
        window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
      };
      if (this.$route.path === "/") {
        scroll();
      } else {
        this.$router.push({ path: "/", hash: "#testimonials" }).then(() => {
          this.$nextTick(() => {
            setTimeout(scroll, 400);
          });
        });
      }
    },
    onSearch() {
      const q = this.searchKeyword.trim();
      if (!q) return;
      this.$router.push({ path: "/search", query: { q } });
      window.scrollTo({ top: 0 });
    },
  },
};
</script>

<style lang="scss" scoped>
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.header {
  width: 100%;
  position: relative;
  z-index: 999;
  background-color: #f6fbff;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.06);
}

.header-inner {
  width: 1400px;
  max-width: calc(100% - 48px);
  margin: 0 auto;
  box-sizing: border-box;
}

.header-top {
  padding: 14px 0;
  border-bottom: 1px solid #eee;

  .header-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
  }
}

.logo-link {
  flex-shrink: 0;
}

.logo-img {
  height: 52px;
  display: block;
}

.header-contact {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 32px;
  min-width: 0;
}

.contact-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #333;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    color: #f18000;
  }
}

.contact-icon {
  font-size: 18px;
  color: #f18000;
}

.header-search {
  flex-shrink: 0;
  display: flex;
  align-items: stretch;
  width: 220px;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid #e0e8f0;
}

.search-input {
  flex: 1;
  min-width: 0;
  padding: 8px 12px;
  font-size: 14px;
  border: none;
  outline: none;
  background: #fff;

  &::placeholder {
    color: #aaa;
  }
}

.search-btn {
  flex-shrink: 0;
  width: 44px;
  border: none;
  background: linear-gradient(145deg, #ffc04d 0%, #ff8c00 48%, #f07800 100%);
  color: #fff;
  font-size: 16px;
  cursor: pointer;

  &:hover {
    opacity: 0.92;
  }
}

.lang-switch {
  flex-shrink: 0;
  padding: 6px 14px;
  line-height: 1;
  color: #333;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: 0.3s;
  white-space: nowrap;

  &:hover {
    background-color: rgba(241, 128, 0, 0.08);
    border-color: #f18000;
    color: #f18000;
  }
}

.header-nav {
  background-color: $box-bg;
}

.nav-inner {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 36px;
  padding: 14px 0;
}

.nav-item {
  position: relative;
  cursor: pointer;
  margin-left: 4rem;

  .nav-title {
    font-size: 15px;
    color: #1a1a1a;
    padding: 4px 0;
    white-space: nowrap;

    i {
      font-size: 12px;
      margin-left: 4px;
    }
  }

  &:hover .nav-title,
  &.nav-item--active .nav-title {
    color: #f18000;
  }

  .nav-subs {
    display: none;
    position: absolute;
    left: 0;
    top: 100%;
    min-width: 140px;
    padding: 6px 0;
    background: #fff;
    border-radius: 6px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    z-index: 10;
    animation: fadeIn 0.2s ease;
  }

  &.nav-item--has-child:hover .nav-subs {
    display: block;
  }

  .nav-sub {
    padding: 10px 16px;
    font-size: 14px;
    color: #333;
    white-space: nowrap;

    &:hover {
      background: rgba(253, 196, 75, 0.15);
      color: #f18000;
    }
  }
}

.navScroll {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

@media (max-width: 1100px) {
  .header-contact {
    display: none;
  }

  .header-search {
    width: 180px;
  }
}

@media (max-width: 768px) {
  .header-top .header-inner {
    flex-wrap: wrap;
  }

  .header-search {
    order: 3;
    width: 100%;
  }

  .nav-inner {
    gap: 8px 20px;
  }
}
</style>
